import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { isArray, isNullOrUndefined, isValidString } from '@pineapplelab/util';
import { Auth, User, UserCredential, getAuth, onIdTokenChanged, signInWithCustomToken } from '@angular/fire/auth';
import { BrowserStorage } from 'src/app/models/browserStorage';
import { ApiService, IApiResponse } from '../api/api.service';
import { UserService } from '../user/user.service';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { take } from 'rxjs';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  auth: { user: any; permissions: number[] } = { user: null, permissions: [] };
  static projectId = environment.projectId;
  apiUrl = environment.API_URL;

  constructor(
    private api: ApiService,
    private userService: UserService,
    private angularFireAuth: AngularFireAuth,
    private router: Router
  ) {}

  async loginWitCustomToken(accessToken: string) {
    const auth = getAuth();
    const userCredential = await signInWithCustomToken(auth, accessToken);
    await this.listenTokenChanges(auth);
    await this.saveUserAndPermissions(userCredential, auth);
  }

  async login(email: string, password: string): Promise<any> {
    try {
      const result = await this.api.post('/auth/login', { user: email, password, projectId: AuthService.projectId });
      if (isNullOrUndefined(result)) {
        return;
      }
      const accessToken = result['accessToken'];
      await this.loginWitCustomToken(accessToken);
      return;
    } catch (err) {
      return 'Usuario o contraseña incorrectos';
    }
  }

  private async saveUserAndPermissions(userCredential: UserCredential, auth: Auth) {
    const [user, _] = await Promise.all([
      this.userService.getUserById(userCredential.user.uid),
      this.assignAuthPermissions(auth.currentUser),
    ]);
    this.auth.user = user;
  }

  private async listenTokenChanges(auth: Auth) {
    onIdTokenChanged(auth, async (user) => {
      if (user) {
        const token = (await user.getIdTokenResult()).token;
        BrowserStorage.set('userToken', token);
      }
    });
  }

  async logout() {
    const auth = getAuth();
    await auth.signOut();
    BrowserStorage.delete('userToken');
    this.auth.user = null;
    this.router.navigateByUrl('/auth/login');
  }

  async isSigned(): Promise<boolean> {
    if (!isValidString(BrowserStorage.get('userToken'))) {
      return false;
    }
    if (this.auth.user) {
      return true;
    }
    const userPromise = new Promise<User>((resolve, reject) => {
      this.angularFireAuth.user.pipe(take(1)).subscribe({
        next: (user) => resolve(user),
      });
    });
    const userLogged = await userPromise;
    if (isNullOrUndefined(userLogged)) {
      return false;
    }
    const user = await this.userService.getUserById(userLogged.uid);
    this.auth.user = user;
    await this.assignAuthPermissions(userLogged);
    return !isNullOrUndefined(user);
  }

  userHasPermissions(permissions: number[]): boolean {
    return permissions.every((permission) => this.auth.permissions.includes(permission));
  }

  private async assignAuthPermissions(user: User) {
    const idToken = await user.getIdTokenResult();
    this.auth.permissions = isArray(idToken.claims['permissions']) ? idToken.claims['permissions'] : [];
  }

  async loginAsOtherUserByUserId(id: string): Promise<string> {
    try {
      const result = await this.api.post('/auth/access-token', { userId: id });
      return result.uid;
    } catch (error) {
      throw error;
    }
  }

  async changePasswordOtherUser(userId: string, newPassword: string) {
    try {
      await this.api.post('/auth/update-password', {
        userId: userId,
        newPassword: newPassword,
        projectId: AuthService.projectId,
      });
    } catch (error) {
      throw error;
    }
  }

  async googleLogin(accessToken: string, idToken: string) {
    try {
      const result = await this.api.post('/auth/googleLogin', {
        accessToken: accessToken,
        idToken: idToken,
        projectId: AuthService.projectId,
      });
      const token = result['accessToken'];
      await this.loginWitCustomToken(token);
    } catch (error) {
      throw error;
    }
  }
}
